/**
 * aerationKnowledge.ts - Cross-site aeration synthesis.
 *
 * Combines documented aeration interventions from the literature (Bautzen,
 * Lake Sempach, Lake Hallwil, Hanningfield, Rutland, Amisk, Black Lake,
 * etc.) into:
 *
 *   1. AERATION_CASES - what was installed, what was observed, what worked
 *   2. SUCCESS_FACTORS - physical parameters that correlate with successful
 *                        outcomes, with the module that predicts each
 *   3. GOOD_BED_INDICATORS - sediment-side criteria that distinguish a
 *                            healthy aerated bed from a hostile anoxic one
 *   4. GOOD_AERATION_INDICATORS - water-column / hardware criteria that
 *                                 distinguish a working aeration scheme
 *   5. DESIGN_TEMPLATE - a synthesised design-parameter starting point
 */

export interface AerationCase {
  id: string;
  site: string;
  country: string;
  systemType: string;
  diffuserType: string;
  installDate: string;
  basinMeanDepth_m: number;
  basinMaxDepth_m: number;
  basinArea_m2: number;
  Q_air_total_Nm3h: number;
  Q_air_perDiffuser_Ls: number;
  diffuserCount: number;
  diffuserDepth_m: number;
  observedOTE_pct: number;
  observedOutcomes: string[];
  whatWorked: string;
  whatFailed: string;
  refs: { citation: string; doi?: string; url?: string; }[];
}

export interface SuccessFactor {
  category: 'Plume' | 'Bed' | 'Hypolimnion O-' | 'Placement' | 'Algae' | 'Compliance';
  parameter: string;
  targetRange: string;
  rationale: string;
  predictedBy: string;            // module id
  evidenceFrom: string[];         // site ids
}

export interface BedHealthIndicator {
  parameter: string;
  unit: string;
  goodRange: string;
  poorRange: string;
  why: string;
  module: string;
}

export interface AerationHealthIndicator {
  parameter: string;
  unit: string;
  goodRange: string;
  poorRange: string;
  why: string;
  module: string;
}

// ------------------------------------------------------------------------
// 1. Cross-site aeration case studies
// ------------------------------------------------------------------------
export const AERATION_CASES: AerationCase[] = [
  {
    id: 'bautzen',
    site: 'Bautzen Reservoir',
    country: 'Saxony, Germany',
    systemType: 'Hypolimnetic aeration (HOLOX-equivalent)',
    diffuserType: 'Fine-bubble membrane discs',
    installDate: '1998 (continuous operation)',
    basinMeanDepth_m: 7.4,
    basinMaxDepth_m: 13.5,
    basinArea_m2: 5_300_000,
    Q_air_total_Nm3h: 2400,
    Q_air_perDiffuser_Ls: 4.0,
    diffuserCount: 168,
    diffuserDepth_m: 6.5,
    observedOTE_pct: 40,
    observedOutcomes: [
      'OTE 32-48 % across operating range (Petzoldt 2010)',
      'Stratification breakdown in 6 d (vs 35 d without aeration)',
      'Microcystis aeruginosa biomass reduced 60 % vs no-aeration baseline year',
      'Internal P load down 85 % within first season (Horn 2011)',
      'Mn breakthrough at WTW eliminated 1999-2010',
    ],
    whatWorked: 'Fine-bubble diffusers placed deep but above sediment scour threshold; centralised blower with PLC-controlled duty cycling',
    whatFailed: 'Initial single-line layout left a dead-zone in the eastern arm - solved by adding two cross-lines in 2002',
    refs: [
      { citation: 'H-lsmann, Rinke & Mooij (2005) Limnologica 35: 304-320', doi: '10.1016/j.limno.2005.07.001' },
      { citation: 'Petzoldt (2010) Wasser & Abfall 12(5): 38-43' },
      { citation: 'Horn et al. (2011) Water Research 45' },
    ],
  },
  {
    id: 'sempach',
    site: 'Lake Sempach',
    country: 'Lucerne, Switzerland',
    systemType: 'Pure oxygen injection (Mixox)',
    diffuserType: 'Liquid-O- stripper, sub-surface discharge',
    installDate: '1984 (still operating)',
    basinMeanDepth_m: 46,
    basinMaxDepth_m: 87,
    basinArea_m2: 14_400_000,
    Q_air_total_Nm3h: 0,        // pure O2, equivalent kg/h reported
    Q_air_perDiffuser_Ls: 0,
    diffuserCount: 1,
    diffuserDepth_m: 30,
    observedOTE_pct: 90,        // pure O2 is much higher
    observedOutcomes: [
      'Hypolimnetic O- raised from 0 to 5 mg/L without breaking stratification',
      'Reduced sediment P release by ~75 % (G-chter & Wehrli 1998)',
      'Mn breakthrough eliminated',
      'No measurable rise in CH- ebullition',
    ],
    whatWorked: 'Pure-O- system maintains hypolimnion oxidised without thermally mixing the lake (preserves cold-water fisheries)',
    whatFailed: 'CAPEX ~ 3-5- a fine-bubble destratifier; only economical for deep lakes with cold-water requirements',
    refs: [
      { citation: 'Stadelmann & W-est (1989) EAWAG Annual Report', },
      { citation: 'G-chter & Wehrli (1998) Environ. Sci. Technol. 32(23): 3659-3665', doi: '10.1021/es980518j' },
    ],
  },
  {
    id: 'hallwil',
    site: 'Lake Hallwil',
    country: 'Aargau, Switzerland',
    systemType: 'Hypolimnetic O- + air bubble plume',
    diffuserType: 'Fine-bubble membrane + LOX injection',
    installDate: '1986 (still operating)',
    basinMeanDepth_m: 28,
    basinMaxDepth_m: 47,
    basinArea_m2: 10_300_000,
    Q_air_total_Nm3h: 600,
    Q_air_perDiffuser_Ls: 2.5,
    diffuserCount: 60,
    diffuserDepth_m: 25,
    observedOTE_pct: 60,
    observedOutcomes: [
      'Hypolimnion DO restored to > 4 mg/L year-round (1990 onward)',
      'CH- ebullition oxidised 60-90 % within rising bubbles (McGinnis 2006)',
      'Internal P load suppressed by > 70 %',
      'Tanytarsus chironomid recovery, indicating bed recovery (M-ller et al. 2003)',
    ],
    whatWorked: 'Combined air + LOX gives high OTE without thermal mixing',
    whatFailed: 'Air-only operation in 1985-1986 caused unwanted thermocline erosion',
    refs: [
      { citation: 'McGinnis, Greinert, Artemov, Beaubien & W-est (2006) J. Geophys. Res. 111: C09007', doi: '10.1029/2005JC003183' },
      { citation: 'M-ller et al. (2003) Helvetica Geologica' },
    ],
  },
  {
    id: 'hanningfield',
    site: 'Hanningfield Reservoir',
    country: 'Essex, UK',
    systemType: 'Helixor air-lift destratifier',
    diffuserType: 'Helixor tubes (perforated PVC, air-lift)',
    installDate: '1972 - earliest UK supply-reservoir aeration',
    basinMeanDepth_m: 7.5,
    basinMaxDepth_m: 17.4,
    basinArea_m2: 3_700_000,
    Q_air_total_Nm3h: 360,
    Q_air_perDiffuser_Ls: 8.0,
    diffuserCount: 12,
    diffuserDepth_m: 15,
    observedOTE_pct: 12,
    observedOutcomes: [
      'Destratification prevented Anabaena flos-aquae blooms (Steel 1972)',
      'WTW taste-and-odour complaints fell 80 % vs pre-1972 baseline',
      'Mn in raw water reduced from 0.4 mg/L to < 0.05 mg/L',
    ],
    whatWorked: 'Coarse-bubble Helixor reliable for many decades - low maintenance',
    whatFailed: 'Low OTE (~12 %) means high electricity demand; superseded by fine-bubble in modern schemes',
    refs: [
      { citation: 'Steel, J.A. (1972) "Reservoir algal productivity" Thames Water Bd' },
      { citation: 'Toms & Drage (1980) Water Research 14: 1135-1146' },
    ],
  },
  {
    id: 'rutland',
    site: 'Rutland Water',
    country: 'Leicestershire, UK',
    systemType: 'Fine-bubble destratification',
    diffuserType: 'Ceramic dome diffusers',
    installDate: '1976; refurbished 2000, 2015',
    basinMeanDepth_m: 9.0,
    basinMaxDepth_m: 35,
    basinArea_m2: 12_500_000,
    Q_air_total_Nm3h: 1800,
    Q_air_perDiffuser_Ls: 3.5,
    diffuserCount: 145,
    diffuserDepth_m: 12,
    observedOTE_pct: 26,
    observedOutcomes: [
      'Anabaena & Microcystis dominance reduced 70 % (Madgwick 2006)',
      'Stratification limited to < 2 weeks per year',
      'Hypolimnion DO > 5 mg/L throughout summer',
      'Geosmin events fell from > 30 ng/L peaks to < 8 ng/L',
    ],
    whatWorked: 'Hex-grid spacing of fine-bubble domes covers full basin; modular layout allows lakeward expansion',
    whatFailed: 'Initial 1976 coarse-bubble layout under-performed - replaced 2000',
    refs: [
      { citation: 'Watts et al. (1989) J. IWEM 3: 532-540' },
      { citation: 'Madgwick et al. (2006) Water Research 40: 1875-1888', doi: '10.1016/j.watres.2006.02.022' },
    ],
  },
  {
    id: 'amisk',
    site: 'Amisk Lake',
    country: 'Alberta, Canada',
    systemType: 'Destratification compressor',
    diffuserType: 'Coarse-bubble line diffuser',
    installDate: '1988 (seasonal)',
    basinMeanDepth_m: 8.4,
    basinMaxDepth_m: 33,
    basinArea_m2: 5_400_000,
    Q_air_total_Nm3h: 700,
    Q_air_perDiffuser_Ls: 5.0,
    diffuserCount: 40,
    diffuserDepth_m: 25,
    observedOTE_pct: 18,
    observedOutcomes: [
      'Hypolimnion O- raised from < 1 to > 5 mg/L (Prepas 1990)',
      'Internal P loading reduced 60 % over 5 years',
      'Walleye and lake trout populations recovered',
    ],
    whatWorked: 'Seasonal operation (Apr-Oct) sufficient - saves OPEX',
    whatFailed: 'Bed scour observed under coarse-bubble lines after 3 years - diffusers raised 0.5 m',
    refs: [
      { citation: 'Prepas, Field, Murphy, Johnson, Burke & Tonn (1997) Can. J. Fish. Aquat. Sci. 54: 2105-2114', doi: '10.1139/f97-127' },
    ],
  },
  {
    id: 'black_lake',
    site: 'Black Lake',
    country: 'Michigan, USA',
    systemType: 'Pure-O- hypolimnetic',
    diffuserType: 'Speece-cone',
    installDate: '1977 (pilot)',
    basinMeanDepth_m: 8.2,
    basinMaxDepth_m: 18,
    basinArea_m2: 41_000_000,
    Q_air_total_Nm3h: 0,
    Q_air_perDiffuser_Ls: 0,
    diffuserCount: 1,
    diffuserDepth_m: 14,
    observedOTE_pct: 88,
    observedOutcomes: [
      'Successful proof-of-concept for Speece-cone deep O- delivery',
      'Hypolimnion held > 6 mg/L for 22 weeks',
      'P-flux from sediment reduced 50 %',
    ],
    whatWorked: 'Speece-cone gives near-100 % OTE with minimal water disturbance',
    whatFailed: 'Pure-O- logistics expensive at remote sites',
    refs: [
      { citation: 'Lorenzen & Fast (1977) US-EPA report EPA-600/3-77-004' },
      { citation: 'Pastorok, Lorenzen & Ginn (1982) Tetra Tech / US-EPA' },
    ],
  },
];

// ------------------------------------------------------------------------
// 2. Common success factors extracted from the cases
// ------------------------------------------------------------------------
export const SUCCESS_FACTORS: SuccessFactor[] = [
  { category: 'Placement', parameter: 'Diffuser depth above bed',
    targetRange: '0.4-1.0 m above sediment-water interface',
    rationale: 'Too close - scour and bed disturbance (Amisk early years); too high - loses depth-resolved OTE benefit',
    predictedBy: 'mehta_partheniades', evidenceFrom: ['amisk', 'bautzen'] },
  { category: 'Placement', parameter: 'Module spacing',
    targetRange: '1.5-2.5 - plume diameter (Type-1 regime)',
    rationale: 'Too tight - plume merging halves coverage (Bautzen 1998 layout); too wide - dead zones',
    predictedBy: 'asaeda_c', evidenceFrom: ['bautzen', 'rutland', 'hanningfield'] },
  { category: 'Placement', parameter: 'Layout pattern',
    targetRange: 'Hexagonal staggered grid, biased to deepest 30 % of footprint',
    rationale: 'Hex packs density without dead zones; deep bias maximises plume vertical reach',
    predictedBy: 'deep_zone', evidenceFrom: ['rutland', 'bautzen'] },
  { category: 'Plume', parameter: 'Asaeda-Imberger C',
    targetRange: '1.6-2.4 (Type-1 to transitional)',
    rationale: 'C < 1.4 = merging dead zone; C > 2.4 = wasted independent plumes',
    predictedBy: 'asaeda_c', evidenceFrom: ['bautzen', 'rutland'] },
  { category: 'Plume', parameter: 'Bubble diameter at release',
    targetRange: '1-3 mm (fine-bubble membrane)',
    rationale: 'Sub-mm gives best OTE but easy to foul; > 5 mm halves OTE (Hanningfield Helixor)',
    predictedBy: 'mcginnis_bubble', evidenceFrom: ['hanningfield', 'rutland', 'bautzen', 'hallwil'] },
  { category: 'Hypolimnion O-', parameter: 'Effective OTE',
    targetRange: '> 25 % for air; > 60 % for LOX',
    rationale: 'Below 25 % - electricity / oxygen OPEX dominates whole-life cost',
    predictedBy: 'mcginnis_bubble', evidenceFrom: ['rutland', 'bautzen', 'sempach', 'hallwil', 'black_lake'] },
  { category: 'Hypolimnion O-', parameter: 'DO at sediment-water interface',
    targetRange: '> 4 mg/L sustained',
    rationale: 'Below 4 mg/L - P, Fe and Mn release ramp up (Davison kinetics); below 2 - full anoxia within days',
    predictedBy: 'davison_fe_mn', evidenceFrom: ['bautzen', 'rutland', 'amisk', 'sempach'] },
  { category: 'Bed', parameter: 'Bed shear -_b at diffuser proximity',
    targetRange: '< 0.6 - -_ce (typically < 0.08 Pa for cohesive)',
    rationale: 'Above this - resuspension of organic-rich top layer - uplift of SOD and P',
    predictedBy: 'mehta_partheniades', evidenceFrom: ['amisk'] },
  { category: 'Bed', parameter: 'Sediment ORP',
    targetRange: '+ 250 mV or higher continuously',
    rationale: 'Below + 100 mV - Fe(III) reduces - bound P + Mn released',
    predictedBy: 'redox_p_release', evidenceFrom: ['bautzen', 'hallwil', 'sempach'] },
  { category: 'Algae', parameter: 'Cyano fraction of phytoplankton',
    targetRange: '< 25 % of biovolume',
    rationale: 'Above this - geosmin / microcystin / lower light penetration - WTW OPEX rises',
    predictedBy: 'csr_groups', evidenceFrom: ['rutland', 'bautzen', 'hanningfield'] },
  { category: 'Compliance', parameter: 'Mn in raw water',
    targetRange: '< 50 -g/L (UK DWI PCV)',
    rationale: 'Mn(II) oxidation kinetics are slow; aeration is the cheapest prevention',
    predictedBy: 'davison_fe_mn', evidenceFrom: ['bautzen', 'sempach', 'hanningfield'] },
  { category: 'Compliance', parameter: 'Geosmin in raw water',
    targetRange: '< 10 ng/L (DWI customer threshold)',
    rationale: 'Above 10 ng/L - PAC dosing at WTW required (-0.5-2 / ML)',
    predictedBy: 'geosmin_predictor', evidenceFrom: ['rutland', 'hanningfield'] },
];

// ------------------------------------------------------------------------
// 3. "Good bed" indicators
// ------------------------------------------------------------------------
export const GOOD_BED_INDICATORS: BedHealthIndicator[] = [
  { parameter: 'Surface sediment ORP', unit: 'mV', goodRange: '+ 250 to + 450', poorRange: '< + 50',
    why: 'Oxic Fe(III) keeps P sorbed; below this Fe(III) - Fe(II) and P/Mn release',
    module: 'redox_p_release' },
  { parameter: 'DO at sediment interface', unit: 'mg/L', goodRange: '> 4 sustained', poorRange: '< 2',
    why: 'Drives Fe/Mn oxidation kinetics; sustains macrofauna bioturbation',
    module: 'davison_fe_mn' },
  { parameter: 'Sediment organic matter (LOI)', unit: '%', goodRange: '< 12', poorRange: '> 25',
    why: 'High OM - high SOD - fast anoxia; aeration buys time but cannot fix bed composition',
    module: 'boudreau_diagenesis' },
  { parameter: 'Macrofaunal density', unit: 'ind/m-', goodRange: '> 1000 (chironomids, tubificids)', poorRange: '< 100',
    why: 'Healthy bioturbation pumps O- into porewater, multiplying effective diffusion 10-100-',
    module: 'aller_bioturbation' },
  { parameter: 'Iron-to-P ratio (Fe:P)', unit: 'molar', goodRange: '> 15', poorRange: '< 5',
    why: 'Excess Fe keeps P bound; below 5:1 P releases even at high ORP (Jensen & Andersen 1992)',
    module: 'redox_p_release' },
  { parameter: 'Bed shear stress -_b', unit: 'Pa', goodRange: '< 0.08 (below cohesive -_ce)', poorRange: '> 0.20',
    why: 'Below -_ce - deposition/stable; above - erosion of organic-rich top layer',
    module: 'mehta_partheniades' },
  { parameter: 'SOD (sediment oxygen demand)', unit: 'g O-/m-/d', goodRange: '< 1.5', poorRange: '> 3.0',
    why: 'High SOD overwhelms even good aeration capacity; must size aerator - SOD - 1.5',
    module: 'nom_sod' },
  { parameter: 'Methane in porewater', unit: 'mmol/L', goodRange: '< 0.5', poorRange: '> 5',
    why: 'High CH- = sulfate-depleted, fully anoxic bed - ebullition risk and CH- GHG forcing',
    module: 'mcginnis_bubble' },
  { parameter: 'Sediment P pool (Fe-bound)', unit: 'mg P/kg dry', goodRange: '< 1000', poorRange: '> 2500',
    why: 'High Fe-bound P - big release on ORP drop; combine aeration with capping for > 2500',
    module: 'sediment_capping' },
];

// ------------------------------------------------------------------------
// 4. "Good aeration" indicators
// ------------------------------------------------------------------------
export const GOOD_AERATION_INDICATORS: AerationHealthIndicator[] = [
  { parameter: 'Effective OTE', unit: '%', goodRange: '> 25 (air) / > 60 (LOX)', poorRange: '< 12',
    why: 'OTE directly drives -/kg-O-. Below 12 % - reconsider diffuser type or depth',
    module: 'mcginnis_bubble' },
  { parameter: 'Plume coverage of basin', unit: '%', goodRange: '> 80', poorRange: '< 50',
    why: 'Dead zones develop bed anoxia even when basin-mean O- is fine',
    module: 'asaeda_c' },
  { parameter: 'Bubble release diameter', unit: 'mm', goodRange: '0.8-3.0', poorRange: '> 5',
    why: 'Fine-bubble dissolves > 3- more O- per bubble than coarse-bubble (Helixor era)',
    module: 'mcginnis_bubble' },
  { parameter: 'Diffuser spacing', unit: '- plume diameter', goodRange: '1.5-2.5', poorRange: '< 1 or > 4',
    why: 'Hex-grid at 1.5-2.5- plume diameter gives full coverage without merging',
    module: 'asaeda_c' },
  { parameter: 'Cost per kg O-', unit: '-/kg', goodRange: '< 1.5', poorRange: '> 3.0',
    why: 'Long-term scheme viability requires WLC < 3 -/kg O- delivered',
    module: 'cost_per_o2' },
  { parameter: 'Stratification breakdown time', unit: 'days', goodRange: '< 10', poorRange: '> 30',
    why: 'Fast destratification suppresses cyanos & restores hypolimnion DO. Bautzen achieved 6 d vs 35 d unaerated',
    module: 'asaeda_c' },
  { parameter: 'Wedderburn number under design wind', unit: '', goodRange: '> 1 (mixed)', poorRange: '< 0.3',
    why: 'Quantifies whether wind tilts SML enough to flush plumes; aeration must dominate W',
    module: 'wind_tilt' },
  { parameter: 'O- supply margin over SOD', unit: '- SOD', goodRange: '> 1.5', poorRange: '< 1.0',
    why: 'Aerator must out-deliver bed demand by 50 % to prevent anoxia returning at design conditions',
    module: 'nom_sod' },
  { parameter: 'Internal seiche period mismatch', unit: 'min',
    goodRange: '> 25 % off any diffuser duty harmonic',
    poorRange: 'within 10 % of a harmonic',
    why: 'Resonance amplifies bed shear and disrupts plume coverage',
    module: 'internal_seiche' },
];

// ------------------------------------------------------------------------
// 5. Synthesised design starting-point template
// ------------------------------------------------------------------------
export interface DesignTemplate {
  scenario: string;
  diffuserType: string;
  poreSize_um: number;
  bubbleSize_mm: number;
  Q_air_perDiffuser_Ls: number;
  spacing_x_plumeDiameter: number;
  layout: string;
  depthAboveBed_m: number;
  targetOTE_pct: number;
  targetMargin_overSOD: number;
}

export const DESIGN_TEMPLATES: DesignTemplate[] = [
  {
    scenario: 'Shallow eutrophic UK reservoir (Barcombe class, 3-6 m)',
    diffuserType: 'Reynolds & Bauhm Diffuser',
    poreSize_um: 1500,
    bubbleSize_mm: 2.5,
    Q_air_perDiffuser_Ls: 2.0,
    spacing_x_plumeDiameter: 1.8,
    layout: 'Hex-grid biased to deepest 30 %',
    depthAboveBed_m: 0.5,
    targetOTE_pct: 28,
    targetMargin_overSOD: 1.6,
  },
  {
    scenario: 'Deep stratified storage (15-40 m)',
    diffuserType: 'Pure O- Mixox / Speece-cone',
    poreSize_um: 0,
    bubbleSize_mm: 1.5,
    Q_air_perDiffuser_Ls: 0,
    spacing_x_plumeDiameter: 2.0,
    layout: 'Single deep cluster + 1-2 perimeter rings',
    depthAboveBed_m: 1.0,
    targetOTE_pct: 80,
    targetMargin_overSOD: 1.4,
  },
  {
    scenario: 'Polymictic mid-size (M-ggelsee class)',
    diffuserType: 'Reynolds & Bauhm Diffuser',
    poreSize_um: 1500,
    bubbleSize_mm: 2.5,
    Q_air_perDiffuser_Ls: 2.5,
    spacing_x_plumeDiameter: 1.7,
    layout: 'Uniform hex over entire basin',
    depthAboveBed_m: 0.5,
    targetOTE_pct: 26,
    targetMargin_overSOD: 1.5,
  },
  {
    scenario: 'Cyano-T/O focus (PAC OPEX driven)',
    diffuserType: 'Reynolds & Bauhm Diffuser (with cap option)',
    poreSize_um: 1000,
    bubbleSize_mm: 2.0,
    Q_air_perDiffuser_Ls: 2.0,
    spacing_x_plumeDiameter: 1.6,
    layout: 'Dense hex grid + La-bentonite cap in deep zone',
    depthAboveBed_m: 0.5,
    targetOTE_pct: 30,
    targetMargin_overSOD: 1.5,
  },
];
